import React, {
  useState,
  useRef,
  useEffect,
  useMemo,
  useCallback,
  SyntheticEvent,
} from "react";
import { useTranslation } from "react-i18next";
import { ViewMode, GanttProps, Task } from "../../types/public-types";
import { GridProps } from "../grid/grid";
import { ganttDateRange, seedDates } from "../../helpers/date-helper";
import { Calendar, CalendarProps } from "../calendar/calendar";
import { TaskGanttContentProps } from "./task-gantt-content";
import { TaskListHeaderDefault } from "../task-list/task-list-header";
import { TaskListTableDefault } from "../task-list/task-list-table";
import {
  StandardTooltipContent,
  Tooltip,
  UserTooltipContent,
} from "../other/tooltip";
import { TaskListProps, TaskList } from "../task-list/task-list";
import { TaskGantt } from "./task-gantt";
import { BarTask } from "../../types/bar-task";
import { convertToBarTasks } from "../../helpers/bar-helper";
import { GanttEvent } from "../../types/gantt-task-actions";
import { DateSetup } from "../../types/date-setup";
import { HorizontalScroll } from "../other/horizontal-scroll";
import { removeHiddenTasks, sortTasks } from "../../helpers/other-helper";
import styles from "./gantt.module.css";
import { ContextManu } from "../other/contextmanu";
import { DepandanyTooltip } from "../other/depandancyTooltip";
import { DepandanyLineTooltip } from "../other/depandancyLineTooltip";
import dropDown from "../../../../svg/dropDown.svg";
import { VerticalScroll } from "../other/vertical-scroll";
import { Workload } from "../workload/workload";

// Assume TaskGantt and Calendar are wrapped in React.forwardRef
// as shown in the prerequisites above.

const GanttComponent: React.FunctionComponent<GanttProps> = ({
  tasks,
  headerHeight = 80,
  columnWidth = 55,
  listCellWidth = "155px",
  rowHeight = 45,
  ganttHeight = 0,
  viewMode = ViewMode.Day,
  preStepsCount = 1,
  locale = "en-GB",
  barFill = 70,
  barCornerRadius = 3,
  barProgressColor = "#a3a3ff",
  barProgressSelectedColor = "#8282f5",
  barBackgroundColor = "#b8c2cc",
  barBackgroundSelectedColor = "#aeb8c2",
  projectProgressColor = "#7db59a",
  projectProgressSelectedColor = "#59a985",
  projectBackgroundColor = "#fac465",
  projectBackgroundSelectedColor = "#f7bb53",
  milestoneBackgroundColor = "#f1c453",
  milestoneBackgroundSelectedColor = "#f29e4c",
  rtl = false,
  handleWidth = 8,
  timeStep = 86400000,
  arrowColor = "grey",
  fontFamily = "Arial, Roboto, Oxygen, Ubuntu, Cantarell, Fira Sans, Droid Sans, Helvetica Neue",
  fontSize = "14px",
  arrowIndent = 15,
  todayColor = "rgba(252, 248, 227, 0.5)",
  viewDate,
  TooltipContent = StandardTooltipContent,
  UserTooltipContentComp = UserTooltipContent,
  ContextMenuContent,
  TaskListHeader = TaskListHeaderDefault,
  TaskListTable = TaskListTableDefault,
  holidays,
  nonWorkingDays,
  onDateChange,
  onProgressChange,
  onDoubleClick,
  onClick,
  onDelete,
  onSelect,
  onExpanderClick,
  onDepandancyDragEnd,
  onDepandanyClick,
  onColorChange,
  columns,
  projectStartDate,
  projectEndDate,
  onContextManuEvent,
  newTask,
  onScroll,
  startEndIndicater = true,
  showContextManu = true,
  canCreateDependency = true,
  showWorkload,
  resourcesTask,
  resourceMember,
  setColumns,
  setSelected,
  taskSorted,
  setIsProject,
  setAPIData,
  apiResourceData,
  inPercentage,
  workloadLoader,
  projectName,
  userAvailabilityByUserId,
  suppressTaskListExpander = false,
  bulkMultiSelectActive = false,
}) => {
  const { t } = useTranslation();
  const rootRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const workLoadRef = useRef<HTMLDivElement>(null);
  const taskListRef = useRef<HTMLDivElement>(null);
  const ganttContainerRef = useRef<HTMLDivElement>(null);

  // State is used for components that need to re-render on scroll change (e.g., scrollbars),
  // but it's only updated *after* the user stops scrolling via debouncing.
  const [workloadScroll, setWorkloadScroll] = useState({ scrollx: 0, sroll: "workload" });
  const scrollYRef = useRef(0);
  const scrollXRef = useRef(-1);

  const [scrollYState, setScrollYState] = useState(0);
  const scrollY = scrollYState;
  const scrollX = scrollXRef.current;

  const setScrollY = (value: number) => {
    if (scrollYRef.current !== value) {
      scrollYRef.current = value;
      setScrollYState(value);
    }
  };

  const setScrollX = (value: number) => {
    if (scrollXRef.current !== value) {
      scrollXRef.current = value;

      setWorkloadScroll((prev) => {
        if (prev.scrollx === value) return prev;
        return { ...prev, scrollx: value };
      });
    }
  };
  const [ignoreScrollEvent, setIgnoreScrollEvent] = useState(false);

  const [dateSetup, setDateSetup] = useState<DateSetup>(() => {
    const [startDate, endDate] = ganttDateRange(tasks, viewMode, preStepsCount);
    const finalEndDate =
      projectEndDate && endDate < projectEndDate ? projectEndDate : endDate;
    return { viewMode, dates: seedDates(startDate, finalEndDate, viewMode) };
  });

  const [currentViewDate, setCurrentViewDate] = useState<Date | undefined>(
    undefined
  );

  const [taskListWidth, setTaskListWidth] = useState(250);
  const [svgContainerWidth, setSvgContainerWidth] = useState(0);
  const [svgContainerHeight, setSvgContainerHeight] = useState(ganttHeight);
  const ganttHeightState = useRef(ganttHeight);
  const [barTasks, setBarTasks] = useState<BarTask[]>([]);
  const [ganttEvent, setGanttEvent] = useState<GanttEvent>({
    action: "",
  });
  const taskHeight = useMemo(
    () => (rowHeight * barFill) / 100,
    [rowHeight, barFill]
  );
  const [selectedTask, setSelectedTask] = useState<BarTask>();
  const [failedTask, setFailedTask] = useState<BarTask | null>(null);
  const svgWidth = dateSetup.dates.length * columnWidth;
  const ganttFullHeight = barTasks.length * rowHeight;
  const [isWorkloadResizing, setIsWorkloadResizing] = useState(false);
  const [selectedTasks, setSelectedTasks] = useState<Task>();
  const [valid, setvalid] = useState<{ start: string; end: string }>();

  // Memoize style/color props for the heavy useEffect dependency
  const barPropsMemo = useMemo(
    () => ({
      columnWidth,
      rowHeight,
      taskHeight,
      barCornerRadius,
      handleWidth,
      rtl,
      barProgressColor,
      barProgressSelectedColor,
      barBackgroundColor,
      barBackgroundSelectedColor,
      projectProgressColor,
      projectProgressSelectedColor,
      projectBackgroundColor,
      projectBackgroundSelectedColor,
      milestoneBackgroundColor,
      milestoneBackgroundSelectedColor,
    }),
    [
      columnWidth,
      rowHeight,
      taskHeight,
      barCornerRadius,
      handleWidth,
      rtl,
      barProgressColor,
      barProgressSelectedColor,
      barBackgroundColor,
      barBackgroundSelectedColor,
      projectProgressColor,
      projectProgressSelectedColor,
      projectBackgroundColor,
      projectBackgroundSelectedColor,
      milestoneBackgroundColor,
      milestoneBackgroundSelectedColor,
    ]
  );

  const handleSelectedProjectHighlight = useCallback(
    (id: string) => {
      if (ganttHeightState.current == 0) {
        return;
      }
      const cleanId = id.replace(/^.*-(?:leaf|lvl)-/, "");
      const bartask = barTasks.find((t) => t.id == cleanId);
      const task = tasks.find((t) => t.id == cleanId);
      if (task) {
        setSelected?.(task);
      }
      setSelectedTasks(bartask);
      setSelectedTask(bartask);

      const taskIndex = barTasks.findIndex((t) => t.id === cleanId);
      if (taskIndex !== -1) {
        // Vertical Scroll
        const taskTop = taskIndex * rowHeight;
        const viewHeight = ganttHeightState.current || ganttHeight;
        let targetScrollY = taskTop - (viewHeight / 2) + (rowHeight / 2);
        const maxScrollY = ganttFullHeight - viewHeight;
        targetScrollY = Math.max(0, Math.min(targetScrollY, maxScrollY));
        setScrollY(targetScrollY);

        // Horizontal Scroll
        if (bartask) {
          const taskXCenter = (bartask.x1 + bartask.x2) / 2;
          const viewWidth = svgContainerWidth || (wrapperRef.current ? (wrapperRef.current.clientWidth - taskListWidth) : 0);
          if (viewWidth > 0) {
            let targetScrollX = taskXCenter - (viewWidth / 2);
            const maxScrollX = svgWidth - viewWidth;
            targetScrollX = Math.max(0, Math.min(targetScrollX, maxScrollX));
            setScrollX(targetScrollX);
          }
        }
      }
    },
    [
      barTasks,
      tasks,
      setSelected,
      setSelectedTask,
      setSelectedTasks,
      rowHeight,
      ganttHeight,
      ganttFullHeight,
      svgContainerWidth,
      taskListWidth,
      svgWidth,
    ]
  );

  const handleSelectedTask = useCallback(
    (taskId: string) => {
      const newSelectedTask = barTasks.find((t) => t.id === taskId);
      const oldSelectedTask = barTasks.find(
        (t) => !!selectedTask && t.id === selectedTask.id
      );
      if (onSelect) {
        if (oldSelectedTask) onSelect(oldSelectedTask, false);
        if (newSelectedTask) onSelect(newSelectedTask, true);
      }
      setSelectedTask(newSelectedTask);
    },
    [barTasks, selectedTask, onSelect, setSelectedTask]
  );

  const handleExpanderClick = useCallback(
    (task: Task) => {
      console.log("calleddd expand");
      if (onExpanderClick && task.hideChildren !== undefined) {
        onExpanderClick({ ...task, hideChildren: !task.hideChildren });
      }
    },
    [onExpanderClick]
  );

  const resize = useCallback((e: MouseEvent) => {
    if (taskListRef.current) {
      const newWidth =
        e.clientX - taskListRef.current.getBoundingClientRect().left;
      if (newWidth > 50) setTaskListWidth(newWidth);
    }
  }, []);

  const stopResizing = useCallback(() => {
    document.removeEventListener("mousemove", resize);
    document.removeEventListener("mouseup", stopResizing);
  }, [resize]);

  const startResizing = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      document.addEventListener("mousemove", resize);
      document.addEventListener("mouseup", stopResizing);
    },
    [resize, stopResizing]
  );

  const handleWorkloadResize = useCallback(
    (e: MouseEvent) => {
      if (!ganttContainerRef.current || !rootRef.current) return;
      const rootBounds = rootRef.current.getBoundingClientRect();
      const ganttContainerTop =
        ganttContainerRef.current.getBoundingClientRect().top;
      let newHeight = e.clientY - ganttContainerTop;
      const minGanttHeight = headerHeight + rowHeight * 2;
      const minWorkloadHeight = 100;
      const resizerHeight = 10;
      const horizontalScrollbarHeight = 20;
      const maxGanttHeight =
        rootBounds.height -
        minWorkloadHeight -
        resizerHeight -
        horizontalScrollbarHeight;
      newHeight = Math.max(minGanttHeight, newHeight);
      newHeight = Math.min(maxGanttHeight, newHeight);
      ganttHeightState.current = newHeight;
      setSvgContainerHeight(newHeight);
      if (wrapperRef.current) {
        wrapperRef.current.style.height = `${newHeight + 100}px`;
      }
    },
    [headerHeight, rowHeight, showWorkload, ganttHeight]
  );

  const stopWorkloadResize = useCallback(() => {
    setIsWorkloadResizing(false);
    document.removeEventListener("mousemove", handleWorkloadResize);
    document.removeEventListener("mouseup", stopWorkloadResize);
  }, [handleWorkloadResize]);

  const startWorkloadResize = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      setIsWorkloadResizing(true);
      document.addEventListener("mousemove", handleWorkloadResize);
      document.addEventListener("mouseup", stopWorkloadResize);
    },
    [handleWorkloadResize, stopWorkloadResize, setIsWorkloadResizing]
  );

  const onDepandancyDraging = useCallback(
    (start: string, end: string) => setvalid({ end: end, start: start }),
    [setvalid]
  );

  const handleScrollY = useCallback(
    (event: SyntheticEvent<HTMLDivElement>) => {
      onScroll(event);
      if (Math.abs(scrollY - event.currentTarget.scrollTop) > 1 && !ignoreScrollEvent) {
        setScrollY(event.currentTarget.scrollTop);
        setIgnoreScrollEvent(true);
      } else {
        setIgnoreScrollEvent(false);
      }
    },
    [onScroll, scrollY, ignoreScrollEvent]
  );

  // eslint-disable-next-line prefer-const
  let [startDate] = ganttDateRange(
    tasks,
    viewMode,
    preStepsCount
  );

  const handleScrollX = useCallback(
    (event: SyntheticEvent<HTMLDivElement>) => {
      const scrollLeft = event.currentTarget.scrollLeft;
      const scrollWidth = event.currentTarget.scrollWidth;
      const clientWidth = event.currentTarget.clientWidth;
      if (scrollLeft + clientWidth >= scrollWidth - 50) {
        setDateSetup((prev) => {
          const endDateForInfinite = new Date(prev.dates[prev.dates.length - 1]);
          let startDateForInfinite = new Date(prev.dates[0]);
          if (isNaN(startDateForInfinite.getTime())) {
            startDateForInfinite = new Date();
          }
          if (viewMode === ViewMode.QuarterYear) {
            startDateForInfinite.setFullYear(
              startDateForInfinite.getFullYear()
            );
          }

          startDateForInfinite.setDate(startDateForInfinite.getDate());
          endDateForInfinite.setDate(endDateForInfinite.getDate() + 10);
          return {
            viewMode: prev.viewMode,
            dates: seedDates(
              startDateForInfinite,
              endDateForInfinite,
              viewMode
            ),
          };
        });
      }

      // if (scrollLeft == 0) {
      //   setDateSetup((prev) => {
      //     startDateForInfinite = new Date(prev.dates[0]);
      //     startDateForInfinite.setDate(startDateForInfinite.getDate() - 10);
      //     const endDateForInfinite = new Date(prev.dates[prev.dates.length - 1]);
      //     return {
      //       viewMode: prev.viewMode,
      //       dates: seedDates(startDateForInfinite, endDateForInfinite, viewMode),
      //     };
      //   });
      // }

      if (Math.abs(scrollX - scrollLeft) > 1 && !ignoreScrollEvent) {
        setScrollX(scrollLeft);
        setIgnoreScrollEvent(true);
      } else {
        setIgnoreScrollEvent(false);
      }
    },
    [scrollX, ignoreScrollEvent, viewMode, setDateSetup, setScrollX, startDate]
  );

  const taskSelect = useCallback(
    (data: Task) => {
      if (data.id === selectedTask?.id) {
        setSelectedTasks(undefined);
        setSelectedTask(undefined);
      } else {
        setSelectedTasks(data);
        setSelectedTask(data as BarTask);
      }
    },
    [selectedTask, setSelectedTasks, setSelectedTask]
  );

  useEffect(() => {
    if (newTask && newTask.id) {
      const bartask = barTasks.find((t) => t.id == newTask.id);
      setSelectedTasks(bartask);
      // setSelectedTask(bartask);
    }
  }, [newTask, barTasks, setSelectedTasks]);

  useEffect(() => {
    ganttHeightState.current = ganttHeight;
    setSvgContainerHeight(ganttHeight);
  }, [ganttHeight]);

  useEffect(() => {
    let filteredTasks: Task[] = onExpanderClick
      ? removeHiddenTasks(tasks)
      : tasks;
    filteredTasks = filteredTasks.sort(sortTasks);

    const [startRange, endDate] = ganttDateRange(
      filteredTasks,
      viewMode,
      preStepsCount
    );

    // const parsedStartDate = new Date(projectStartDate);
    // const isValidStart = !isNaN(parsedStartDate.getTime());
    // const starDate = isValidStart ? parsedStartDate : new Date(startRange);

    // starDate.setDate(starDate.getDate() - 2);
    // starDate.setHours(0, 0, 0, 0);

    // const startDateToUse = starDate;
    const finalEndDate =
      projectEndDate && endDate < projectEndDate ? projectEndDate : endDate;

    const newDates = seedDates(startRange, finalEndDate, viewMode);

    if (rtl) {
      newDates.reverse();
      if (scrollX === -1) {
        setScrollX(newDates.length * columnWidth);
      }
    }
    if (viewMode === ViewMode.Month) {
      const endDateExtended = new Date(endDate);
      endDateExtended.setFullYear(endDateExtended.getFullYear() + 2);
      setDateSetup({
        viewMode,
        dates: seedDates(startRange, endDateExtended, viewMode),
      });
    } else if (viewMode === ViewMode.QuarterYear) {
      const endDateExtended = new Date(endDate);
      endDateExtended.setFullYear(endDateExtended.getFullYear() + 5);
      setDateSetup({
        viewMode,
        dates: seedDates(startRange, endDateExtended, viewMode),
      });
    } else if (viewMode === ViewMode.Week) {
      const endDateExtended = new Date(endDate);
      endDateExtended.setFullYear(endDateExtended.getFullYear() + 3);
      startRange.setFullYear(startRange.getFullYear());
      setDateSetup({
        viewMode,
        dates: seedDates(startRange, endDateExtended, viewMode),
      });
    } else if (viewMode === ViewMode.Year) {
      const endDateExtended = new Date(endDate);
      endDateExtended.setFullYear(endDateExtended.getFullYear() + 20);
      setDateSetup({
        viewMode,
        dates: seedDates(startRange, endDateExtended, viewMode),
      });
    } else {
      setDateSetup({ dates: newDates, viewMode });
    }
    setBarTasks(
      convertToBarTasks(
        filteredTasks,
        newDates,
        barPropsMemo.columnWidth,
        barPropsMemo.rowHeight,
        barPropsMemo.taskHeight,
        barPropsMemo.barCornerRadius,
        barPropsMemo.handleWidth,
        barPropsMemo.rtl,
        barPropsMemo.barProgressColor,
        barPropsMemo.barProgressSelectedColor,
        barPropsMemo.barBackgroundColor,
        barPropsMemo.barBackgroundSelectedColor,
        barPropsMemo.projectProgressColor,
        barPropsMemo.projectProgressSelectedColor,
        barPropsMemo.projectBackgroundColor,
        barPropsMemo.projectBackgroundSelectedColor,
        barPropsMemo.milestoneBackgroundColor,
        barPropsMemo.milestoneBackgroundSelectedColor
      )
    );
  }, [
    tasks,
    viewMode,
    preStepsCount,
    rtl,
    onExpanderClick,
    projectEndDate,
    barPropsMemo,
    columnWidth, // Keep columnWidth for scrollX update logic
  ]);

  useEffect(() => {
    if (
      viewMode === dateSetup.viewMode &&
      ((viewDate && !currentViewDate) ||
        (viewDate && currentViewDate?.valueOf() !== viewDate.valueOf()))
    ) {
      const dates = dateSetup.dates;
      const index = dates.findIndex(
        (d, i) =>
          viewDate.valueOf() >= d.valueOf() &&
          i + 1 !== dates.length &&
          viewDate.valueOf() < dates[i + 1].valueOf()
      );
      if (index === -1) {
        return;
      }
      setCurrentViewDate(viewDate);
      setScrollX(columnWidth * index);
    }
  }, [viewDate, columnWidth, dateSetup, viewMode, currentViewDate]);

  // Other standard useEffects
  useEffect(() => {
    const { changedTask, action } = ganttEvent;
    if (changedTask) {
      if (action === "delete") {
        setGanttEvent({ action: "" });
        setBarTasks(barTasks.filter((t) => t.id !== changedTask.id));
      } else if (
        action === "move" ||
        action === "end" ||
        action === "start" ||
        action === "progress"
      ) {
        const prevStateTask = barTasks.find((t) => t.id === changedTask.id);
        if (
          prevStateTask &&
          (prevStateTask.start.getTime() !== changedTask.start.getTime() ||
            prevStateTask.end.getTime() !== changedTask.end.getTime() ||
            prevStateTask.progress !== changedTask.progress)
        ) {
          const newTaskList = barTasks.map((t) =>
            t.id === changedTask.id ? changedTask : t
          );
          setBarTasks(newTaskList);
        }
      }
    }
  }, [ganttEvent, barTasks]);

  useEffect(() => {
    // const handleLeftClick = (event: MouseEvent) => {
    //   if (event.button === 0) {
    //     setGanttEvent({
    //       action: "",
    //       changedTask: undefined,
    //       originalSelectedTask: undefined,
    //     });
    //   }
    // };

    const handleMouseLeave = () => {
      setGanttEvent({
        action: "",
        changedTask: undefined,
        originalSelectedTask: undefined,
      });
    };

    const node = wrapperRef.current;
    if (node) {
      node.addEventListener("mouseleave", handleMouseLeave);
    }
    // document.addEventListener("mouseup", handleLeftClick);

    return () => {
      // document.removeEventListener("mouseup", handleLeftClick);
      if (node) {
        node.removeEventListener("mouseleave", handleMouseLeave);
      }
    };
  }, []);

  useEffect(() => {
    if (failedTask) {
      setBarTasks(
        barTasks.map((t) => (t.id !== failedTask.id ? t : failedTask))
      );
      setFailedTask(null);
    }
  }, [failedTask, barTasks]);

  useEffect(() => {
    if (!listCellWidth) {
      setTaskListWidth(0);
    } else if (taskListRef.current) {
      setTaskListWidth(taskListRef.current.offsetWidth);
    }
  }, [listCellWidth]);

  useEffect(() => {
    if (wrapperRef.current) {
      setSvgContainerWidth(wrapperRef.current.offsetWidth - taskListWidth);
    }
  }, [taskListWidth]);

  useEffect(() => {
    if (ganttHeightState.current) {
      setSvgContainerHeight(ganttHeightState.current + headerHeight);
    } else {
      setSvgContainerHeight(tasks.length * rowHeight + headerHeight);
    }
  }, [ganttHeightState.current, tasks.length, headerHeight, rowHeight]);

  // SCROLL REFS
  useEffect(() => {
    const handleWheel = (event: WheelEvent) => {
      // event.deltaMode === 1 means DOM_DELTA_LINE (traditional mouse wheel)
      // event.deltaMode === 0 means DOM_DELTA_PIXEL (trackpad)
      const isLineDelta = event.deltaMode === 1;
      const scrollSpeedFactor = isLineDelta ? 30 : 0.6; // Tune these values for fine control

      const component =
        (event.target as HTMLElement).getAttribute("data-component") ==
        "workload";
      if (event.shiftKey || event.deltaX) {
        const delta = event.deltaX || event.deltaY;
        const scrollMove = delta * scrollSpeedFactor;
        let newScrollX = scrollX + scrollMove;
        if (newScrollX < 0) {
          newScrollX = 0;
        } else if (newScrollX > svgWidth) {
          newScrollX = svgWidth;
        }
        setScrollX(newScrollX);

        // Trigger horizontal pagination
        if (wrapperRef.current) {
          const clientWidth = wrapperRef.current.clientWidth - taskListWidth;
          if (newScrollX + clientWidth >= svgWidth - 50) {
            setDateSetup((prev) => {
              const endDateForInfinite = new Date(prev.dates[prev.dates.length - 1]);
              let startDateForInfinite = new Date(prev.dates[0]);
              if (isNaN(startDateForInfinite.getTime())) {
                startDateForInfinite = new Date();
              }
              if (viewMode === ViewMode.QuarterYear) {
                startDateForInfinite.setFullYear(
                  startDateForInfinite.getFullYear()
                );
              }

              startDateForInfinite.setDate(startDateForInfinite.getDate());
              endDateForInfinite.setDate(endDateForInfinite.getDate() + 10);
              return {
                viewMode: prev.viewMode,
                dates: seedDates(
                  startDateForInfinite,
                  endDateForInfinite,
                  viewMode
                ),
              };
            });
          }
        }
        event.preventDefault();
      } else if (ganttHeightState.current && !component) {
        let newScrollY = scrollY + (event.deltaY * scrollSpeedFactor);
        const maxScrollY = ganttFullHeight - ganttHeightState.current;
        if (newScrollY < 0) {
          newScrollY = 0;
        } else if (newScrollY > maxScrollY) {
          newScrollY = maxScrollY;
        }

        if (newScrollY !== scrollY) {
          setScrollY(newScrollY);
          if (onScroll) {
            const fakeEvent = {
              currentTarget: {
                scrollTop: newScrollY,
                scrollHeight: ganttFullHeight,
                clientHeight: ganttHeightState.current,
              },
            } as any;
            onScroll(fakeEvent);
          }
        }
        event.preventDefault();
      }

      setIgnoreScrollEvent(true);
    };

    // subscribe if scroll is necessary
    ganttContainerRef.current?.addEventListener("wheel", handleWheel, {
      passive: false,
    });
    return () => {
      ganttContainerRef.current?.removeEventListener("wheel", handleWheel);
    };
  }, [scrollY, scrollX, svgWidth, ganttFullHeight]);

  useEffect(() => {
    if (wrapperRef.current) {
      if (showWorkload) {
        ganttHeightState.current = ganttHeight / 2;
        wrapperRef.current.style.height = `${ganttHeight / 2 + 100}px`;
      }
      if (!showWorkload) {
        ganttHeightState.current = ganttHeight;
        wrapperRef.current.style.height = `${ganttHeight + 100}px`;
      }
    }
  }, [showWorkload, ganttHeight]);

  // const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
  //   let newScrollY = scrollY;
  //   let newScrollX = scrollX;
  //   let isX = true;
  //   switch (event.key) {
  //     case "Down":
  //     case "ArrowDown":
  //       newScrollY += rowHeight;
  //       isX = false;
  //       break;
  //     case "Up":
  //     case "ArrowUp":
  //       newScrollY -= rowHeight;
  //       isX = false;
  //       break;
  //     case "Left":
  //     case "ArrowLeft":
  //       newScrollX -= columnWidth;
  //       break;
  //     case "Right":
  //     case "ArrowRight":
  //       newScrollX += columnWidth;
  //       break;
  //     default:
  //       return;
  //   }
  //   if (isX) {
  //     if (newScrollX < 0) newScrollX = 0;
  //     else if (newScrollX > svgWidth) newScrollX = svgWidth;
  //     setScrollX(newScrollX);
  //   } else {
  //     if (newScrollY < 0) newScrollY = 0;
  //     else if (newScrollY > ganttFullHeight - ganttHeightState.current)
  //       newScrollY = ganttFullHeight - ganttHeightState.current;
  //     setScrollY(newScrollY);
  //   }
  // };

  const gridProps: GridProps = useMemo(
    () => ({
      columnWidth,
      svgWidth,
      tasks: barTasks,
      rowHeight,
      dates: dateSetup.dates,
      todayColor,
      rtl,
      nonWorkingDays,
      holidays,
      projectStartDate,
      projectEndDate,
      viewMode,
      startEndIndicater,
      sete: () => { },
      setEndDrag: () => { },
      scrollY,
      ganttHeight: ganttHeightState.current,
    }),
    [
      columnWidth,
      svgWidth,
      barTasks,
      rowHeight,
      dateSetup.dates,
      todayColor,
      rtl,
      nonWorkingDays,
      holidays,
      projectStartDate,
      projectEndDate,
      viewMode,
      startEndIndicater,
      scrollY,
      // ganttHeightState.current is a ref, so it doesn't strictly need to be in deps, but we add it if needed. 
      // Instead, we just use ganttHeight state if it exists, or just omit it from deps since it's a ref.
    ]
  );

  const calendarProps: CalendarProps = useMemo(
    () => ({
      dateSetup,
      locale,
      viewMode,
      headerHeight,
      columnWidth,
      fontFamily,
      fontSize,
      rtl,
      projectStartDate,
      projectEndDate,
      startEndIndicater,
    }),
    [
      dateSetup,
      locale,
      viewMode,
      headerHeight,
      columnWidth,
      fontFamily,
      fontSize,
      rtl,
      projectStartDate,
      projectEndDate,
      startEndIndicater,
    ]
  );

  const tableProps: TaskListProps = useMemo(
    () => ({
      rowHeight,
      rowWidth: listCellWidth,
      fontFamily,
      fontSize,
      tasks: barTasks,
      locale,
      headerHeight,
      scrollY,
      ganttHeight: ganttHeightState.current,
      horizontalContainerClass: styles.horizontalContainer,
      selectedTask,
      taskListRef,
      taskListWidth,
      setSelectedTask: handleSelectedTask,
      onExpanderClick: handleExpanderClick,
      TaskListHeader: TaskListHeader as any,
      TaskListTable: TaskListTable as any,
      taskDblClick: onDoubleClick,
      columns,
      setCurrentColumns: setColumns,
      setSortedTask: taskSorted,
      taskSelect,
      suppressTaskListExpander,
      bulkMultiSelectActive,
    }),
    [
      rowHeight,
      listCellWidth,
      fontFamily,
      fontSize,
      barTasks,
      locale,
      headerHeight,
      scrollY,
      selectedTask,
      taskListWidth,
      handleSelectedTask,
      handleExpanderClick,
      TaskListHeader,
      TaskListTable,
      onDoubleClick,
      columns,
      setColumns,
      taskSorted,
      taskSelect,
      suppressTaskListExpander,
      bulkMultiSelectActive,
    ]
  );

  const barProps: TaskGanttContentProps & any = useMemo(
    () => ({
      tasks: barTasks,
      dates: dateSetup.dates,
      ganttEvent,
      selectedTask,
      rowHeight,
      taskHeight,
      columnWidth,
      arrowColor,
      timeStep,
      fontFamily,
      fontSize,
      arrowIndent,
      svgWidth,
      rtl,
      setGanttEvent,
      setFailedTask,
      setSelectedTask: handleSelectedTask,
      onDateChange,
      onProgressChange,
      onDoubleClick,
      onClick,
      onDelete,
    }),
    [
      barTasks,
      dateSetup.dates,
      ganttEvent,
      selectedTask,
      rowHeight,
      taskHeight,
      columnWidth,
      arrowColor,
      timeStep,
      fontFamily,
      fontSize,
      arrowIndent,
      svgWidth,
      rtl,
      setGanttEvent,
      setFailedTask,
      handleSelectedTask,
      onDateChange,
      onProgressChange,
      onDoubleClick,
      onClick,
      onDelete,
    ]
  );
  const workloadCalender = (
    <div style={{ position: "sticky", top: 0, zIndex: 0 }}>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width={gridProps.svgWidth}
        height={calendarProps.headerHeight}
        fontFamily={barProps.fontFamily}
      >
        <Calendar
          {...calendarProps}
        />
      </svg>
    </div>
  );

  if (!tasks || tasks.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center w-full h-[350px] bg-white rounded-2xl border border-slate-150 p-8 shadow-[0_2px_8px_-3px_rgba(0,0,0,0.05)] my-4">
        <div className="text-slate-300 text-5xl mb-3">🔍</div>
        <h3 className="text-base font-bold text-slate-700 mb-1">{t("No tasks found")}</h3>
        <p className="text-xs text-slate-400 text-center max-w-xs">
          {t("We couldn't find any tasks matching your search query.")}
        </p>
      </div>
    );
  }

  return (
    <div
      ref={rootRef}
      className="gantt relative"
      style={{
        display: "flex",
        flexDirection: "column",
        height: "99%",
        overflow: "hidden",
      }}
    >
      <div ref={ganttContainerRef}>
        <div
          className={styles.wrapper}
          tabIndex={0}
          ref={wrapperRef}
          style={{
            position: "relative",
          }}
        >
          {listCellWidth && (
            <div className="relative group " data-gantt-tasklist>
              {/* "data-gantt-tasklist" is used for downloading pdf without task list */}
              <TaskList {...tableProps} />{" "}
              <div
                id="dragbar"
                className="hover:scale-x-125 scale-x-95 hover:!bg-gray-300 transition-transform duration-300"
                style={{
                  top: 0,
                  height: "100%",
                  right: 0,
                  width: "5px",
                  background: "#80808044",
                  position: "absolute",
                  cursor: "col-resize",
                  transition: "0.3s ease-in-out",
                }}
                onMouseDown={startResizing}
              ></div>
              <div
                className="opacity-0 group-hover:opacity-100 select-none transition-opacity duration-150  rounded-full"
                style={{
                  top: "50%",
                  height: "45px",
                  right: 0,
                  width: "45px",
                  position: "absolute",
                  cursor: "pointer",
                  transform: "translate(44%,-15%)",
                }}
              >
                <div className="flex h-full w-full gap-1 p-1 justify-center items-center text-xl text-white bg-[#e5e5e5] rounded-full font-semibold">
                  <div
                    className="text-gray-700 opacity-65 hover:text-emerald-700 hover:opacity-100 flex items-center justify-center "
                    onClick={() => setTaskListWidth(8)}
                  >
                    <img
                      src={dropDown}
                      className="w-6 min-w-6 -rotate-90"
                      alt="collapse"
                    />
                  </div>
                  {wrapperRef && wrapperRef.current && (
                    <div
                      className="text-gray-700 opacity-65 hover:text-emerald-700 flex items-center hover:opacity-100 justify-center"
                      onClick={() =>
                        setTaskListWidth(
                          (wrapperRef.current?.clientWidth ?? 250) - 10
                        )
                      }
                    >
                      <img
                        src={dropDown}
                        className="w-6 min-w-6 rotate-90"
                        alt="expand"
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          <TaskGantt
            canCreateDependency={canCreateDependency}
            gridProps={gridProps}
            calendarProps={calendarProps}
            barProps={barProps}
            ganttHeight={ganttHeightState.current}
            scrollY={scrollY}
            scrollX={scrollX}
            viewMode={viewMode}
            onDepandancyDragEnd={onDepandancyDragEnd}
            onDepandancyDraging={(s, e) => onDepandancyDraging(s.type, e.type)}
            depandanyData={onDepandanyClick}
            selectedTasks={selectedTasks}
            onselect={(data) => {
              setScrollX(data.x);
              setScrollY(data.y);
            }}
            svgWidth={svgWidth}
          />

          {ganttEvent.changedTask &&
            ganttEvent.action == "mouseenter" &&
            !ganttEvent.optional_data && (
              <Tooltip
                arrowIndent={arrowIndent}
                rowHeight={rowHeight}
                svgContainerHeight={svgContainerHeight}
                svgContainerWidth={svgContainerWidth}
                fontFamily={fontFamily}
                fontSize={fontSize}
                task={ganttEvent.changedTask}
                headerHeight={headerHeight}
                taskListWidth={taskListWidth}
                TooltipContent={TooltipContent}
                rtl={rtl}
                svgWidth={svgWidth}
                wrapperRef={wrapperRef}
              />
            )}
          {ganttEvent.changedTask &&
            ganttEvent.action == "mouseenter" &&
            Boolean(ganttEvent.optional_data?.userId) && (
              <Tooltip
                arrowIndent={arrowIndent}
                rowHeight={rowHeight}
                svgContainerHeight={svgContainerHeight}
                svgContainerWidth={svgContainerWidth}
                fontFamily={fontFamily}
                fontSize={fontSize}
                task={
                  {
                    ...ganttEvent.changedTask,
                    efforts: ganttEvent.optional_data.efforts,
                  } as any
                }
                headerHeight={headerHeight}
                taskListWidth={taskListWidth}
                TooltipContent={UserTooltipContentComp}
                userId={
                  ganttEvent.optional_data
                    ? (ganttEvent.optional_data.userId as string)
                    : ""
                }
                rtl={rtl}
                svgWidth={svgWidth}
                wrapperRef={wrapperRef}
              />
            )}
          {ganttEvent.changedTask &&
            ganttEvent.originalSelectedTask &&
            ganttEvent.action === "dragging" && (
              <DepandanyTooltip
                endTask={ganttEvent.changedTask}
                task={ganttEvent.originalSelectedTask}
                valid={valid}
                wrapperRef={wrapperRef}
              />
            )}
          {ganttEvent.changedTask &&
            ganttEvent.originalSelectedTask &&
            ganttEvent.action === "hoverOnDependance" && (
              <DepandanyLineTooltip
                endTask={ganttEvent.changedTask}
                task={ganttEvent.originalSelectedTask}
                wrapperRef={wrapperRef}
              />
            )}
          {ganttEvent.changedTask &&
            ganttEvent.action === "contextmenu" &&
            showContextManu && (
              <ContextManu
                onEventCall={onContextManuEvent}
                arrowIndent={arrowIndent}
                rowHeight={rowHeight}
                svgContainerHeight={svgContainerHeight}
                svgContainerWidth={svgContainerWidth}
                fontFamily={fontFamily}
                fontSize={fontSize}
                task={ganttEvent.changedTask}
                headerHeight={headerHeight}
                taskListWidth={taskListWidth}
                rtl={rtl}
                svgWidth={svgWidth}
                onColorChange={onColorChange}
                ContexContent={ContextMenuContent}
                remove={() => {
                  setGanttEvent({ action: "" });
                }}
                wrapperRef={wrapperRef}
              />
            )}

          <VerticalScroll
            ganttFullHeight={ganttFullHeight}
            ganttHeight={ganttHeightState.current}
            headerHeight={headerHeight}
            scroll={scrollY}
            onScroll={handleScrollY}
            rtl={rtl}
          />
        </div>
      </div>

      {showWorkload && (
        <>
          <div
            onMouseDown={startWorkloadResize}
            className="group"
            style={{
              height: "5px",
              cursor: "row-resize",
              backgroundColor: isWorkloadResizing ? "#a1a1a1" : "#e0e0e0",
              borderTop: "1px solid #ccc",
              borderBottom: "1px solid #ccc",
              transition: "background-color 0.2s",
              userSelect: "none",
              position: "relative",
            }}
          >
            <div
              className="opacity-0 group-hover:opacity-100 select-none transition-opacity duration-150 z-10"
              style={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%) rotate(90deg)",
                height: "45px",
                width: "45px",
                cursor: "pointer",
              }}
            >
              <div className="flex h-full w-full gap-1 p-1 justify-center items-center text-xl text-white bg-[#e5e5e5] rounded-full font-semibold">
                <div
                  className="text-gray-700 opacity-65 hover:text-emerald-700 hover:opacity-100 flex items-center justify-center"
                  onClick={() => {
                    ganttHeightState.current = 0;
                    wrapperRef.current!.style.height = `${0}px`;
                  }}
                >
                  <img
                    src={dropDown}
                    className="w-6 min-w-6"
                    style={{ transform: "rotate(-90deg)" }}
                    alt="collapse"
                  />
                </div>
                {wrapperRef?.current && (
                  <div
                    className="text-gray-700 opacity-65 hover:text-emerald-700 hover:opacity-100 flex items-center justify-center"
                    onClick={() => {
                      ganttHeightState.current = ganttHeight + 50;
                      wrapperRef.current!.style.height = `${ganttHeight + 50
                        }px`;
                    }}
                  >
                    <img
                      src={dropDown}
                      className="w-6 min-w-6"
                      style={{ transform: "rotate(90deg)" }}
                      alt="expand"
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
          <div
            style={{ flex: 1, height: "100%", overflow: "auto" }}
            ref={workLoadRef}
          >
            <Workload
              projectName={projectName}
              tasks={resourcesTask as any}
              resourceMember={resourceMember as any}
              dates={dateSetup.dates}
              svgWidth={svgWidth}
              columnWidth={columnWidth}
              rowHeight={rowHeight}
              headerHeight={headerHeight}
              taskListWidth={taskListWidth}
              scrollX={workloadScroll.scrollx}
              fontFamily={fontFamily}
              fontSize={fontSize}
              nonWorkingDays={nonWorkingDays ?? []}
              holidays={holidays ?? []}
              onScroll={(data) => {
                const el = data.currentTarget
                // Workload has a native horizontal scrollbar. If user drags it natively, 
                // we want to sync Main ScrollX.
                // We use Math.abs tolerance to prevent a jitter-loop between scrollX and el.scrollLeft.
                if (Math.abs(scrollX - el.scrollLeft) > 5) {
                  setScrollX(el.scrollLeft);
                }
              }}
              workloadLoader={workloadLoader}
              isPercentage={inPercentage}
              workloadCalender={workloadCalender}
              showCalender={ganttHeightState}
              viewMode={viewMode}
              handleSelectedProjectHighlight={handleSelectedProjectHighlight}
              setIsProject={setIsProject}
              setAPIData={(data: any) =>
                setAPIData && setAPIData({ ...data, dates: dateSetup.dates })
              }
              apiResourceData={apiResourceData}
              userAvailabilityByUserId={userAvailabilityByUserId}
            />
          </div>
        </>
      )}

      <HorizontalScroll
        svgWidth={svgWidth}
        taskListWidth={taskListWidth}
        scroll={scrollX}
        rtl={rtl}
        rootRef={rootRef}
        onScroll={handleScrollX}
      />
    </div>
  );
};

export const Gantt = React.memo(GanttComponent);